import { inject, Injectable } from '@angular/core';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Observable } from 'rxjs';

import { GridService, IdDescription, GridFieldOptions } from 'tms-lib';
import { LocationSelectRendererComponent } from '../../components/grids/cell-renderers/location-select-renderer/location-select-renderer.component';
import { ProductRendererComponent } from '../../components/grids/cell-renderers/product-renderer/product-renderer.component';
import { ProductQuantityRendererComponent } from '../../components/grids/cell-renderers/product-quantity-renderer/product-quantity-renderer.component';
import { ProductSearchRendererComponent } from '../../components/grids/cell-renderers/product-search-renderer/product-search-renderer.component';

@Injectable({
  providedIn: 'root',
})
export class TmsWebGridService extends GridService {
  private readonly gridService = inject(GridService);

  public load() {
    this.gridService.components = {
      ...this.components,
      locationSelectRenderer: LocationSelectRendererComponent,
      productRenderer: ProductRendererComponent,
      productQuantityRenderer: ProductQuantityRendererComponent,
      productSearchRenderer: ProductSearchRendererComponent,
    };
  }

  locationSelect(
    field: string,
    headerName: string,
    selectOptions?: Observable<IdDescription[]> | IdDescription[],
    options?: GridFieldOptions,
  ): ColDef {
    if (this.menuService.isReadOnly) {
      return this.text(field, headerName, options);
    }

    return {
      ...this.text(field, headerName, options),
      cellRenderer: 'locationSelectRenderer',
      cellRendererParams: {
        context: { options: this.utilityService.mapSelectOptions(selectOptions) },
      } as ICellRendererParams,
    };
  }

  product(
    keyField: string,
    descriptionField: string,
    productTypeField: string,
    headerName: string,
    options?: GridFieldOptions,
  ): ColDef {
    return {
      ...this.text(keyField, headerName, options),
      cellRenderer: 'productRenderer',
      cellRendererParams: {
        descriptionField,
        productTypeField,
      },
      width: 300,
      filter: false,
      sortable: false,
      suppressAutoSize: true,
      suppressColumnsToolPanel: true,
      ...options,
    };
  }

  productQuantity(
    quantityField: string,
    productTypeField: string,
    unitOfMeasureField: string,
    priceField: string,
    currencyField: string,
    headerName: string,
    addButtonText: string,
    addIcon: string,
    sampleOutOfStockMessage: string,
    update: (model: any) => void,
    add: (model: any) => void,
    minus: (model: any) => void,
    remove: (model: any) => void,
    options?: GridFieldOptions,
    showNumber?: boolean,
  ): ColDef {
    return {
      ...this.text(quantityField, headerName, options),
      cellRenderer: 'productQuantityRenderer',
      cellRendererParams: {
        productTypeField,
        unitOfMeasureField,
        priceField,
        currencyField,
        update,
        add,
        minus,
        remove,
        addIcon,
        addButtonText,
        sampleOutOfStockMessage,
        showNumber,
      },
      filter: false,
      sortable: false,
      suppressAutoSize: true,
      suppressColumnsToolPanel: true,
      ...options,
    };
  }

  productSearch(selected?: (model: any) => Observable<any>, show?: (data: any) => boolean): ColDef {
    const field = 'productSearch';
    const headerName = $localize`Product Search`;
    return {
      ...this.text(field, headerName),
      cellRenderer: 'productSearchRenderer',
      cellRendererParams: {
        selected,
        show,
      },
    };
  }
}
